import React, {useState} from 'react';
import {Button, DatePicker, Select, Space} from 'antd';
import {useQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import dayjs, {Dayjs} from 'dayjs';
import BatchInputFilterItem from '../../components/BatchInputFilterItem';
import {
    useFilterContainerClassName,
    useFilterInputContainerClassName,
    useFilterLabelClassName,
} from '../../../../utils/languageUtils';
import {getSmartLabelClassName} from '../../../../utils/labelWidthUtils';
import {gql} from '@apollo/client';
import {
    GET_DRIVER_OPTIONS,
    GetDriverOptionsResponse,
    GetDriverOptionsVariables,
    formatDriverOptionLabel,
} from '../../../../graphql/queries/getDriverOptions';
import {
    GET_COMPLAINT_DSP_OPTIONS,
    GetComplaintDspOptionsResponse,
    GetComplaintDspOptionsVariables,
} from '../graphql/queries/getComplaintDspOptions';
import {useWarehouseOptions} from '../../../../hooks/useWarehouseOptions';
import type {ComplaintSourceFilter} from '../utils/complaintSource';

/** 远程搜索模式下触发查询的最小关键字长度；过短会让全美司机查询失去收窄意义 */
const DRIVER_SEARCH_MIN_LENGTH = 2;

/** 客诉违规原因枚举（一期 DNR + OTHER 兜底，由后端 ComplaintType 单一来源下发） */
const GET_COMPLAINT_TYPE_LIST = gql`
    query GetComplaintTypeList {
        getComplaintTypeList
    }
`;

const {RangePicker} = DatePicker;

export interface CustomerComplaintFilters {
    /** 运单号原始字符串；支持以 ; , 换行分隔的多值 */
    trackingNumbers?: string;
    /** 仓库 ID（下拉单选）；仅 HQ 视角可选，wms/DSP 的仓库来自顶部选择器 */
    warehouseId?: number;
    /** DSP ID（下拉单选）；wms / HQ 视角可选，DSP 视角由登录态决定不可选 */
    dspId?: number;
    /** 司机 account ID（下拉单选） */
    driverId?: number;
    complaintType?: string;
    /** UI 层的来源（客服 / 系统）；提交给后端时按映射展开 */
    source?: ComplaintSourceFilter;
    createdAt?: [Dayjs, Dayjs];
    appealSubmittedAt?: [Dayjs, Dayjs];
    /** 完结时间区间（仅"已结案"tab 下有意义） */
    finishedAt?: [Dayjs, Dayjs];
}

interface CustomerComplaintFilterProps {
    filters: CustomerComplaintFilters;
    onChange: (filters: CustomerComplaintFilters) => void;
    onSearch: () => void;
    onReset: () => void;
    /** 当前账户绑定的 dspId；仅 DSP 视角可用 */
    dspId: number | null;
    /** 当前账户选中的仓库 ID；wms 视角必填 */
    warehouseId: number | null;
    /** 是否仅查看视角（wms / HQ）—决定调哪个司机查询接口 */
    isViewOnly: boolean;
    /** HQ 全美视角：下发时间不可清空（后端强制校验时间范围必填） */
    isHq?: boolean;
    /** 额外操作按钮（如导出），与查询 / 重置同排右对齐 */
    extraActions?: React.ReactNode;
}

const CustomerComplaintFilter: React.FC<CustomerComplaintFilterProps> = ({
    filters,
    onChange,
    onSearch,
    onReset,
    dspId,
    warehouseId,
    isViewOnly,
    isHq = false,
    extraActions,
}) => {
    const {t} = useTranslation();

    const containerClassName = useFilterContainerClassName();
    const inputContainerClassName = useFilterInputContainerClassName();

    const labelTrackingNumbers = String(t('customerComplaint.filter.trackingNumbers'));
    const labelWarehouse = String(t('customerComplaint.filter.warehouse'));
    const labelDsp = String(t('customerComplaint.filter.dsp'));
    const labelDriverId = String(t('customerComplaint.filter.driverId'));
    const labelComplaintType = String(t('customerComplaint.filter.complaintType'));
    const labelSource = String(t('customerComplaint.filter.source'));
    const labelCreatedAt = String(t('customerComplaint.filter.createdAt'));
    const labelAppealSubmittedAt = String(t('customerComplaint.filter.appealSubmittedAt'));
    const labelFinishedAt = String(t('customerComplaint.filter.finishedAt'));

    // 3 列对齐
    const column1Labels = [labelDriverId, labelCreatedAt];
    const column2Labels = [labelComplaintType, labelAppealSubmittedAt];
    const column3Labels = [labelSource, labelFinishedAt];

    const labelCls1 = useFilterLabelClassName(getSmartLabelClassName(column1Labels));
    const labelCls2 = useFilterLabelClassName(getSmartLabelClassName(column2Labels));
    const labelCls3 = useFilterLabelClassName(getSmartLabelClassName(column3Labels));

    // 违规原因：调后端枚举接口，单一来源；前端只负责 i18n 翻译。
    // 过滤掉 OTHER —— OTHER 是数据层兜底（外部系统传未识别类型时落库），不作为筛选选项暴露给用户
    const {data: complaintTypeResp, loading: complaintTypeLoading} = useQuery<{
        getComplaintTypeList: string[];
    }>(GET_COMPLAINT_TYPE_LIST, {fetchPolicy: 'cache-first'});
    const complaintTypeOptions = (complaintTypeResp?.getComplaintTypeList ?? [])
        .filter((code) => code !== 'OTHER')
        .map((code) => ({
            value: code,
            label: t(`customerComplaint.type.${code}`, code) as string,
        }));

    // 来源 UI 仅暴露 客服 / 系统 两个聚合选项；提交时由 Panel 展开为 EMAIL/PHONE/OTHER
    const sourceOptions: {value: ComplaintSourceFilter; label: string}[] = [
        {value: 'CUSTOMER_SERVICE', label: t('customerComplaint.source.CUSTOMER_SERVICE') as string},
        {value: 'SYSTEM', label: t('customerComplaint.source.SYSTEM') as string},
    ];

    // 仓库下拉：仅 HQ 视角需要（wms / DSP 的仓库来自顶部选择器）
    const {warehouseOptions, loading: warehouseOptionsLoading} = useWarehouseOptions();
    // DSP 下拉：wms 用顶部选择器的仓库收窄，HQ 用筛选器里选的仓库；都没有则给全部
    const dspScopeWarehouseId = isHq ? filters.warehouseId : warehouseId ?? undefined;
    const {data: dspResp, loading: dspOptionsLoading} = useQuery<
        GetComplaintDspOptionsResponse,
        GetComplaintDspOptionsVariables
    >(GET_COMPLAINT_DSP_OPTIONS, {
        variables: {warehouseId: dspScopeWarehouseId ?? null},
        skip: !isViewOnly && !isHq,
        fetchPolicy: 'cache-first',
    });
    const dspOptions = (dspResp?.getComplaintDspOptions ?? []).map((d) => ({
        value: d.id,
        label: d.dspCode ? `${d.name}（${d.dspCode}）` : d.name,
    }));

    // 司机筛选的生效范围：DSP 视角锁定登录态 dspId；wms/HQ 用筛选器里选的 DSP，
    // 仓库则 HQ 用筛选器、wms 用顶部选择器（HQ 的 warehouseId prop 恒为 null）
    const effectiveDspId = isViewOnly ? filters.dspId : dspId ?? undefined;
    const effectiveWarehouseId = isHq ? filters.warehouseId : warehouseId ?? undefined;
    /**
     * 司机下拉双模式：
     *   - 已选仓库或 DSP：候选集有限，一次拉回交给 antd 本地过滤，输入不再发请求
     *   - 两者都没选（仅 HQ）：候选集是全美司机且后端有条数上限，本地过滤会漏，故走远程搜索
     */
    const isDriverRemoteMode = !effectiveWarehouseId && !effectiveDspId;
    const [driverKeyword, setDriverKeyword] = useState('');
    const {data: driverResp, loading: driversLoading} = useQuery<
        GetDriverOptionsResponse,
        GetDriverOptionsVariables
    >(GET_DRIVER_OPTIONS, {
        variables: {
            warehouseId: effectiveWarehouseId ?? null,
            dspIds: effectiveDspId ? [effectiveDspId] : null,
            keyword: isDriverRemoteMode ? driverKeyword : null,
        },
        // 远程模式下关键字太短就不查，避免每敲一个字母打一次全美查询
        skip: isDriverRemoteMode && driverKeyword.trim().length < DRIVER_SEARCH_MIN_LENGTH,
        fetchPolicy: 'cache-first',
    });
    // 离职司机保留可选：其名下历史客诉仍需可检索，仅在文案上标注
    const driverOptions = (driverResp?.getDriverOptions ?? []).map((d) => ({
        value: d.accountId,
        label: formatDriverOptionLabel(d, t('customerComplaint.filter.driverResigned')),
    }));

    // 周期上限 90 天，避免大区间扫库
    const buildRangeDisabledDate = () => (current: Dayjs, info?: {from?: Dayjs}) => {
        if (!current || !info?.from) return false;
        return Math.abs(current.diff(info.from, 'day')) > 90;
    };

    return (
        <div className="mb-2 py-2">
            {/* 运单号独占一行 */}
            <div className="mb-4">
                <BatchInputFilterItem
                    label={labelTrackingNumbers}
                    value={filters.trackingNumbers}
                    placeholder={t('customerComplaint.filter.trackingNumbersPlaceholder') as string}
                    onChange={(e) =>
                        onChange({
                            ...filters,
                            trackingNumbers: e.target.value || undefined,
                        })
                    }
                    rows={2}
                    spanClassName={getSmartLabelClassName(column1Labels)}
                />
            </div>

            {/*
                筛选项统一放在同一个三列网格里按顺序流式排布，各视角只是可见项不同：
                仓库仅 HQ 有（wms / DSP 的仓库由顶部选择器决定），DSP 在 wms / HQ 可选。
                不给它们单开一行——否则视角不同会出现半空的行，与下方网格错位。
            */}
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                {isHq && (
                    <div className="min-w-0">
                        <div className={containerClassName}>
                            <span className={labelCls1}>{labelWarehouse}</span>
                            <div className={inputContainerClassName}>
                                <Select<number>
                                    size="middle"
                                    className="w-full"
                                    options={warehouseOptions.map((w) => ({
                                        value: Number(w.id),
                                        label: w.name,
                                    }))}
                                    value={filters.warehouseId}
                                    placeholder={t('customerComplaint.filter.warehousePlaceholder') as string}
                                    // 换仓库时清空已选司机：司机候选集随仓库变化，留着会出现「司机不在该仓」的矛盾态
                                    onChange={(value) =>
                                        onChange({...filters, warehouseId: value ?? undefined, driverId: undefined})
                                    }
                                    loading={warehouseOptionsLoading}
                                    showSearch
                                    optionFilterProp="label"
                                    allowClear
                                />
                            </div>
                        </div>
                    </div>
                )}
                {(isHq || isViewOnly) && (
                    <div className="min-w-0">
                        <div className={containerClassName}>
                            <span className={labelCls2}>{labelDsp}</span>
                            <div className={inputContainerClassName}>
                                <Select<number>
                                    size="middle"
                                    className="w-full"
                                    options={dspOptions}
                                    value={filters.dspId}
                                    placeholder={t('customerComplaint.filter.dspPlaceholder') as string}
                                    // 同上：换 DSP 时清空已选司机
                                    onChange={(value) =>
                                        onChange({...filters, dspId: value ?? undefined, driverId: undefined})
                                    }
                                    loading={dspOptionsLoading}
                                    showSearch
                                    optionFilterProp="label"
                                    allowClear
                                />
                            </div>
                        </div>
                    </div>
                )}

                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={labelCls1}>{labelDriverId}</span>
                        <div className={inputContainerClassName}>
                            <Select<number>
                                size="middle"
                                className="w-full"
                                options={driverOptions}
                                value={filters.driverId}
                                placeholder={
                                    (isDriverRemoteMode
                                        ? t('customerComplaint.filter.driverIdSearchPlaceholder')
                                        : t('customerComplaint.filter.driverIdPlaceholder')) as string
                                }
                                onChange={(value) => onChange({...filters, driverId: value ?? undefined})}
                                loading={driversLoading}
                                showSearch
                                // 远程模式：候选集是全美司机的截断结果，本地过滤会漏，交给后端 keyword 查；
                                // 本地模式：候选集已是完整范围，用 label 本地过滤，输入时不再发请求
                                filterOption={!isDriverRemoteMode}
                                optionFilterProp={isDriverRemoteMode ? undefined : 'label'}
                                onSearch={isDriverRemoteMode ? setDriverKeyword : undefined}
                                notFoundContent={
                                    isDriverRemoteMode && driverKeyword.trim().length < DRIVER_SEARCH_MIN_LENGTH
                                        ? (t('customerComplaint.filter.driverSearchHint') as string)
                                        : undefined
                                }
                                allowClear
                            />
                        </div>
                    </div>
                </div>
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={labelCls2}>{labelComplaintType}</span>
                        <div className={inputContainerClassName}>
                            <Select
                                size="middle"
                                className="w-full"
                                options={complaintTypeOptions}
                                value={filters.complaintType}
                                placeholder={t('customerComplaint.filter.complaintTypePlaceholder') as string}
                                onChange={(value) => onChange({...filters, complaintType: value || undefined})}
                                loading={complaintTypeLoading}
                                allowClear
                            />
                        </div>
                    </div>
                </div>
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={labelCls3}>{labelSource}</span>
                        <div className={inputContainerClassName}>
                            <Select<ComplaintSourceFilter>
                                size="middle"
                                className="w-full"
                                options={sourceOptions}
                                value={filters.source}
                                placeholder={t('customerComplaint.filter.sourcePlaceholder') as string}
                                onChange={(value) => onChange({...filters, source: value ?? undefined})}
                                allowClear
                            />
                        </div>
                    </div>
                </div>
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={labelCls1}>{labelCreatedAt}</span>
                        <div className={inputContainerClassName}>
                            <RangePicker
                                size="middle"
                                className="w-full"
                                value={filters.createdAt ?? null}
                                disabledDate={buildRangeDisabledDate()}
                                // HQ 全美查询时间范围必填：禁止清空（后端同样强制校验，双保险）
                                allowClear={!isHq}
                                onChange={(range) => {
                                    if (range && range[0] && range[1]) {
                                        onChange({...filters, createdAt: [range[0], range[1]]});
                                    } else if (!isHq) {
                                        onChange({...filters, createdAt: undefined});
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={labelCls2}>{labelAppealSubmittedAt}</span>
                        <div className={inputContainerClassName}>
                            <RangePicker
                                size="middle"
                                className="w-full"
                                value={filters.appealSubmittedAt ?? null}
                                disabledDate={buildRangeDisabledDate()}
                                onChange={(range) => {
                                    if (range && range[0] && range[1]) {
                                        onChange({...filters, appealSubmittedAt: [range[0], range[1]]});
                                    } else {
                                        onChange({...filters, appealSubmittedAt: undefined});
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={labelCls3}>{labelFinishedAt}</span>
                        <div className={inputContainerClassName}>
                            <RangePicker
                                size="middle"
                                className="w-full"
                                value={filters.finishedAt ?? null}
                                disabledDate={buildRangeDisabledDate()}
                                onChange={(range) => {
                                    if (range && range[0] && range[1]) {
                                        onChange({...filters, finishedAt: [range[0], range[1]]});
                                    } else {
                                        onChange({...filters, finishedAt: undefined});
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex justify-end items-center mt-3">
                <Space>
                    {extraActions}
                    <Button onClick={onReset} className="w-auto min-w-24">
                        <span className="font-bold">{t('customerComplaint.filter.reset')}</span>
                    </Button>
                    <Button type="primary" onClick={onSearch} className="w-auto min-w-24">
                        <span className="font-bold">{t('customerComplaint.filter.search')}</span>
                    </Button>
                </Space>
            </div>
        </div>
    );
};

export const getDefaultCustomerComplaintFilters = (): CustomerComplaintFilters => ({
    // 下发时间默认最近一个月
    createdAt: [
        dayjs().subtract(1, 'month').startOf('day'),
        dayjs().endOf('day'),
    ],
});

export default CustomerComplaintFilter;
